import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Monitor, Phone, CalendarCheck, Receipt, PiggyBank } from "lucide-react";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useProductViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import HeroVisualCard from "@/components/HeroVisualCard";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

// ─── Approved copy ──────────────────────────────────────────────────────
// Source: APPROVED_COPY-D2D7BD23DCDB (Gary Cameron, 10 September 2026).
// Section wording below is approved verbatim — do not reword, and do not add
// claims about security, PCI scope, pricing, savings, contract length,
// hardware, supported devices, user counts, settlement speed or availability
// without separately dated approved evidence.

const BOOK_CTA = "/book";

// Situations named in the approved section-2 body copy. Labels only — no
// additional claim is made about any of them.
const SITUATIONS = [
  { icon: PiggyBank, label: "Deposits" },
  { icon: CalendarCheck, label: "Bookings" },
  { icon: Receipt, label: "Invoice payments" },
];

// The three checks named in the approved section-5 body copy, kept in the
// order Gary approved them.
const DISCOVERY_CHECKS = [
  "How you take payments now",
  "Who needs access",
  "The kinds of phone payments you expect to take",
];

// Visible FAQ content. The same array is passed to SEOHead so the FAQPage
// JSON-LD can never drift from what is rendered on the page.
const FAQS = [
  {
    q: "Do I need an online shop?",
    a: "A virtual terminal is used by your team through a web browser, so the customer does not need to visit an online shop.",
  },
  {
    q: "Can it work with my current payment setup?",
    a: "It may be possible to add CanDo for this specific need. We'll check your current arrangements before recommending anything.",
  },
  {
    q: "What will it cost?",
    a: "The right setup and costs depend on your business and how you plan to use it. We'll explain the options before you make a decision.",
  },
];

const CTA_BASE =
  "inline-flex items-center gap-2 rounded-full font-semibold text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-aqua focus-visible:ring-offset-2";

/**
 * Section CTA. Every CTA on this page points at /book — the single approved
 * conversion destination for the Virtual Terminal copy.
 */
function BookCTA({ children, variant = "primary", testId }) {
  const styles = {
    primary: "bg-brand-aqua hover:bg-brand-aqua-dark text-white px-8 py-3.5",
    secondary:
      "border border-brand-aqua text-brand-aqua hover:bg-brand-aqua-light px-7 py-3",
    inverse: "bg-white hover:bg-brand-aqua-light text-brand-black px-8 py-3.5",
  };
  return (
    <Link to={BOOK_CTA} className={`${CTA_BASE} ${styles[variant]}`} data-testid={testId}>
      {children}
      <ArrowRight size={16} aria-hidden="true" />
    </Link>
  );
}

export default function VirtualTerminalPage() {
  useProductViewed("phone_payments");
  return (
    <div>
      <SEOHead
        title="Virtual Terminal for Phone Payments"
        description="Take card payments over the phone using a virtual terminal. Talk to CanDo about deposits, bookings and remote payments for your business."
        path="/virtual-terminal"
        faqs={FAQS}
        service={{
          name: "Virtual Terminal",
          description:
            "A browser-based way for a UK business to enter payment details while speaking with a customer by phone.",
          serviceType: "Virtual Terminal",
        }}
      />

      {/* 1. Hero */}
      <section className="hero-gradient py-16 md:py-24" data-testid="vt-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" labels={{ "virtual-terminal": "Phone Payments" }} />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Virtual Terminal</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                Take card payments <span className="text-brand-aqua">over the phone</span> when your customer isn't there in person
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
                When a customer is ready to pay but cannot be there in person, a virtual terminal gives your team a browser-based way to take the payment during the call. It can be useful for deposits, bookings and outstanding invoices.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <BookCTA testId="vt-cta-hero">Book a phone-payment demo</BookCTA>
              </motion.div>
            </motion.div>
            <HeroVisualCard
              icon={Monitor}
              accentIcons={[PiggyBank, CalendarCheck, Receipt]}
              accentLabels={["Deposits", "Bookings", "Invoices"]}
              testId="vt-hero-visual"
            />
          </div>
        </div>
      </section>

      {/* 2. Customer situations */}
      <section className="py-16 md:py-20 bg-white" data-testid="vt-situations">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">
              Built for the moments when payment happens away from the counter
            </h2>
            <p className="text-brand-grey leading-relaxed mb-8">
              A customer may want to secure a booking, pay a deposit or settle an invoice while you have them on the phone. A virtual terminal gives your team a practical way to handle those conversations without sending the customer to an online shop.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {SITUATIONS.map((s) => (
                <li key={s.label} className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light">
                  <s.icon size={20} className="text-brand-aqua flex-shrink-0" aria-hidden="true" />
                  <span className="text-sm font-semibold text-brand-black">{s.label}</span>
                </li>
              ))}
            </ul>
            <BookCTA variant="secondary" testId="vt-cta-situations">See whether it fits your business</BookCTA>
          </motion.div>
        </div>
      </section>

      {/* 3. Product explanation */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="vt-explainer">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">
              What is a virtual terminal?
            </h2>
            <p className="text-lg text-brand-black leading-relaxed font-medium mb-8">
              It is a browser-based way for your team to enter payment details while speaking with a customer by phone. CanDo will confirm the right setup, eligibility and operating requirements for your business before you decide.
            </p>
            <BookCTA variant="secondary" testId="vt-cta-explainer">Talk through your setup</BookCTA>
          </motion.div>
        </div>
      </section>

      {/* 4. Existing setup */}
      <section className="py-16 md:py-20 bg-white" data-testid="vt-existing-setup">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">
              Keep what works. Add the right tool for the job.
            </h2>
            <p className="text-brand-grey leading-relaxed mb-8">
              You may be able to keep your current setup and add CanDo where it solves a specific payment problem. We will confirm what works for your business, rather than asking you to change more than you need to.
            </p>
            <BookCTA variant="secondary" testId="vt-cta-existing-setup">Check what can work with your setup</BookCTA>
          </motion.div>
        </div>
      </section>

      {/* 5. Discovery conversation */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="vt-discovery">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-6">
              What we'll check with you
            </h2>
            <ol className="space-y-3 mb-6">
              {DISCOVERY_CHECKS.map((check, i) => (
                <li key={check} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-brand-aqua text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">{i + 1}</span>
                  <span className="text-brand-black leading-relaxed">{check}</span>
                </li>
              ))}
            </ol>
            <p className="text-brand-grey leading-relaxed mb-8">
              Then we'll explain the available setup, costs and onboarding steps before you decide.
            </p>
            <BookCTA variant="secondary" testId="vt-cta-discovery">Book a phone-payment demo</BookCTA>
          </motion.div>
        </div>
      </section>

      {/* 6. FAQs */}
      <section className="py-16 md:py-20 bg-white" data-testid="vt-faqs">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-8">
            Questions businesses usually ask before a demo
          </h2>
          <ResponsiveAccordion items={FAQS} testIdPrefix="vt-faq" className="mb-8" />
          <BookCTA variant="secondary" testId="vt-cta-faqs">Ask CanDo about phone payments</BookCTA>
        </div>
      </section>

      <LocationCrossLinks />
      <RelatedReviews
        product="virtual-terminal"
        eyebrow="Customer Stories"
        heading="See what our customers say about our phone payment service"
        subheading="Real feedback from UK businesses taking payments over the phone with CanDo."
        testId="virtual-terminal-related-reviews"
      />

      {/* 7. Final call to action */}
      <section className="py-16 md:py-20 bg-brand-black text-white" data-testid="vt-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.div variants={fadeUp} className="flex justify-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                <Phone size={26} className="text-brand-aqua" aria-hidden="true" />
              </div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4">
              Let's see whether a virtual terminal fits your business
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/80 leading-relaxed mb-8">
              Tell us how you collect deposits, booking payments or invoice payments today. We'll talk through the practical options and confirm the next step that makes sense for your business.
            </motion.p>
            <motion.div variants={fadeUp} className="flex justify-center">
              <BookCTA variant="inverse" testId="vt-cta-final">Book a phone-payment demo</BookCTA>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
